import { useMemo } from 'react'
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts'
import type { AnimationTiming } from 'recharts/types/util/types'
import { ChartCard } from './ChartCard'
import { getChartColorCss } from '../constants/chartColors'
import { CHART_ANIMATION } from '../constants/chartAnimation'
import type { AggregatedChartData } from '../types'

export interface TreemapChartProps {
  readonly data: ReadonlyArray<AggregatedChartData>
  readonly title?: string
  readonly subtitle?: string
  readonly colorOffset?: number
  readonly maxItems?: number
}

// Felton Chart Colors (RGBA format for alpha support)
const TREEMAP_COLORS_FILL = [
  'rgba(82, 132, 239, 0.15)',    // chart-1: Indigo (Blue)
  'rgba(77, 219, 200, 0.15)',    // chart-2: Teal (Cyan)
  'rgba(246, 174, 49, 0.15)',    // chart-3: Amber (Orange)
  'rgba(245, 86, 113, 0.15)',    // chart-4: Rose (Pink)
  'rgba(146, 92, 240, 0.15)',    // chart-5: Violet (Purple)
  'rgba(40, 215, 133, 0.15)',    // chart-6: Emerald (Green)
  'rgba(53, 183, 243, 0.15)',    // chart-7: Sky (Light Blue)
] as const

const TREEMAP_COLORS_STROKE = [
  'rgba(82, 132, 239, 0.5)',     // chart-1
  'rgba(77, 219, 200, 0.5)',     // chart-2
  'rgba(246, 174, 49, 0.5)',     // chart-3
  'rgba(245, 86, 113, 0.5)',     // chart-4
  'rgba(146, 92, 240, 0.5)',     // chart-5
  'rgba(40, 215, 133, 0.5)',     // chart-6
  'rgba(53, 183, 243, 0.5)',     // chart-7
] as const

/**
 * Treemap Chart - Block-based area visualization
 *
 * Proportional blocks for categorical data (ethnicity, etc.).
 * Each block's area represents its value.
 *
 * Features:
 * - Capsule DNA: Rounded corners on rectangles
 * - Color-coded by index (Felton palette)
 * - Labels inside blocks (when space permits)
 * - Animated entry (700ms bounce)
 */
export const TreemapChart: React.FC<TreemapChartProps> = ({
  data,
  title = 'Treemap',
  subtitle,
  colorOffset = 0,
  maxItems = 10,
}) => {
  const chartData = useMemo(() => {
    // Sort by value and take top N
    const sorted = [...data]
      .sort((a, b) => b.value - a.value)
      .slice(0, maxItems)

    // Recharts Treemap expects 'size' field
    // Use colorOffset for variety across different treemaps
    return sorted.map((item, index) => ({
      name: item.label,
      size: item.value,
      fill: getChartColorCss(index + colorOffset),
    }))
  }, [data, maxItems, colorOffset])

  if (chartData.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle}>
        <div className="flex items-center justify-center min-h-[160px] text-v11-text-muted text-sm">
          No data available for this period
        </div>
      </ChartCard>
    )
  }

  // Custom content renderer - Based on user's reference design
  const CustomizedContent = (props: any) => {
    const { x, y, width, height, name, value, depth, index } = props

    // Only render depth 1, skip if too small
    if (!value || !width || !height || depth !== 1) return null

    const gap = 6 // Space between boxes (structured margins)
    const cornerRadius = width < 30 || height < 30 ? 8 : 16  // 16px for large cards, 8px for small

    // Get color by index (wraps around if more than 7 items)
    const colorIndex = (index + colorOffset) % TREEMAP_COLORS_FILL.length
    const fillColor = TREEMAP_COLORS_FILL[colorIndex]
    const strokeColor = TREEMAP_COLORS_STROKE[colorIndex]

    // Calculate percentage
    const total = chartData.reduce((sum, item) => sum + item.size, 0)
    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : ''

    // Responsive display logic
    const showValue = width > 40 && height > 40      // Hide numbers in very small cards
    const showName = width > 80 && height > 50
    const showPercentage = width > 150 && height > 100

    // Dynamic font size: 24px for large cards, 18px for medium
    const valueFontSize = width > 100 ? 24 : 18

    return (
      <g>
        {/* Box with semi-transparent fill (15% opacity) and colored border (50% opacity) */}
        <rect
          x={x + gap / 2}
          y={y + gap / 2}
          width={width - gap}
          height={height - gap}
          rx={cornerRadius}
          ry={cornerRadius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={2}
          style={{ cursor: 'pointer' }}
        />

        {/* Value - Adaptive text color (dark in light mode, light in dark mode), Outfit 24px/18px, font-weight 700 */}
        {showValue && (
          <text
            x={x + width / 2}
            y={y + height / 2 - (height > 60 ? 4 : 0)}
            textAnchor="middle"
            fill="hsl(var(--v11-text-primary))"
            fontSize={valueFontSize}
            fontWeight="700"
            fontFamily="Outfit, Inter, sans-serif"
            stroke="none"
            strokeWidth={0}
            className="pointer-events-none"
            style={{ letterSpacing: '0.15px', WebkitTextStroke: '0' }}
          >
            {value?.toLocaleString() || '0'}
          </text>
        )}

        {/* Category name - Adaptive text color */}
        {showName && (
          <text
            x={x + width / 2}
            y={y + height / 2 + 14}
            textAnchor="middle"
            fill="hsl(var(--v11-text-primary))"
            fontSize={width > 120 ? 10 : 9}
            fontWeight="500"
            stroke="none"
            strokeWidth={0}
            className="pointer-events-none"
            style={{ WebkitTextStroke: '0' }}
          >
            {name}
          </text>
        )}

        {/* Percentage - Adaptive text color, only for large blocks */}
        {showPercentage && (
          <text
            x={x + width / 2}
            y={y + height / 2 + 30}
            textAnchor="middle"
            fill="hsl(var(--v11-text-primary))"
            fontSize={10}
            fontWeight="bold"
            stroke="none"
            strokeWidth={0}
            className="pointer-events-none"
            style={{ WebkitTextStroke: '0' }}
          >
            {percentage}
          </text>
        )}
      </g>
    )
  }

  return (
    <ChartCard title={title} subtitle={subtitle}>
      <ResponsiveContainer width="100%" height={280}>
        <Treemap
          data={chartData}
          dataKey="size"
          aspectRatio={4 / 3}
          stroke="hsl(var(--v11-surface))"
          fill="#8884d8"
          content={<CustomizedContent />}
          animationDuration={CHART_ANIMATION.duration}
          animationEasing={CHART_ANIMATION.easing as AnimationTiming}
          isAnimationActive={true}
        >
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--v11-surface))',
              border: '1px solid hsl(var(--v11-border))',
              borderRadius: '12px',
              padding: '8px 12px',
            }}
            labelStyle={{
              color: 'hsl(var(--v11-text-primary))',
              fontWeight: 600,
              marginBottom: '4px',
            }}
            formatter={(value: number) => value.toLocaleString()}
          />
        </Treemap>
      </ResponsiveContainer>
    </ChartCard>
  )
}
